import { AddressFieldMiddleware, useAddressFieldCompletion } from '@adornis/address/client/AddressFieldMiddleware.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { FileInput } from '@adornis/file-utils/client/file-input.js';
import { DateInput } from '@adornis/formfield/fields/date-input.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { ToggleInput } from '@adornis/formfield/fields/toggle-input.js';
import type { FunctionalForm } from '@adornis/forms/use-form.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { FormGridEntry } from '@martinus/styles/client/FormGridEntry.js';
import { sgNonOptional } from '@martinus/validation/sgNonOptional.js';
import { html, nothing } from 'lit';
import { SchwerbehinderungsGrad } from '../../../../db/enums/misc-enums.js';
import { Dienststelle } from '../../../../db/static-data/dienststellenschlüssel.js';
import type { Person } from '../../../entities/person.js';
import { ANSchwerbehinderung } from '../../entities/employee-sections/an-schwerbehinderung.js';

export const EmployeeSectionSchwerbehinderungForm = stateful((props: { form: FunctionalForm<EntityData<Person>> }) => {
  const addressCompletion = useAddressFieldCompletion({ language: 'de' });

  return html`
    <div class="form-grid">
      ${FormGridEntry({
        label: 'Schwerbehinderung',
        render: ToggleInput({
          ...props.form.field('mitarbeiterDaten', 'anSchwerbehinderung', 'istVorhanden'),
          label: props.form.document.mitarbeiterDaten?.anSchwerbehinderung?.istVorhanden
            ? 'Schwerbehinderung eingetragen'
            : 'Schwerbehinderung eintragen',
          onPicked: value => {
            if (
              value &&
              props.form.document?.mitarbeiterDaten &&
              !props.form.document.mitarbeiterDaten.anSchwerbehinderung
            ) {
              props.form.document.mitarbeiterDaten.anSchwerbehinderung = new ANSchwerbehinderung({});
            }
            props.form.field('mitarbeiterDaten', 'anSchwerbehinderung', 'istVorhanden').onPicked?.(value);
          },
        }),
      })}
      ${
        props.form.document.mitarbeiterDaten?.anSchwerbehinderung?.istVorhanden
          ? html`
              ${FormGridEntry({
                label: 'Grad der Behinderung',
                render: DropdownSelectionInput({
                  ...props.form.field('mitarbeiterDaten', 'anSchwerbehinderung', 'grad'),
                  placeholder: 'Kein Grad der Behinderung hinterlegt',
                  placeholderMode: 'hide',
                  selectables: Object.values(SchwerbehinderungsGrad),
                  clearable: true,
                }),
              })}
              ${FormGridEntry({
                label: 'Dienststelle',
                render: DropdownSelectionInput({
                  ...props.form.field('mitarbeiterDaten', 'anSchwerbehinderung', 'dienststelle'),
                  placeholder: 'Keine Dienststelle hinterlegt',
                  placeholderMode: 'hide',
                  selectables: Object.values(Dienststelle).map(d => d.Bezeichnung),
                  clearable: true,
                }),
              })}
              ${FormGridEntry({
                label: 'Sitz der Dienststelle',
                render: html`
                  <div class="flex flex-row gap-2">
                    ${DropdownSelectionInput(
                      AddressFieldMiddleware({
                        ...props.form.field('mitarbeiterDaten', 'anSchwerbehinderung', 'dienststelleZip', 'zip'),
                        fieldType: 'zip',
                        addressCompletion,
                        clearable: true,
                        placeholder: 'Postleitzahl',
                        placeholderMode: 'prefix',
                        freeText: true,
                      }),
                    )}
                    ${DropdownSelectionInput(
                      AddressFieldMiddleware({
                        ...props.form.field('mitarbeiterDaten', 'anSchwerbehinderung', 'dienststelleZip', 'city'),
                        fieldType: 'city',
                        addressCompletion,
                        clearable: true,
                        placeholder: 'Stadt',
                        placeholderMode: 'prefix',
                        freeText: true,
                      }),
                    )}
                  </div>
                `,
              })}
              ${FormGridEntry({
                label: 'Gültigkeit des Ausweises',
                render: html`
                  <div class="flex flex-row gap-2">
                    ${DateInput({
                      ...props.form.field('mitarbeiterDaten', 'anSchwerbehinderung', 'gueltigVon'),
                      placeholder: 'Ausweis gültig ab',
                      placeholderMode: 'prefix',
                    })}
                    ${DateInput({
                      ...props.form.field('mitarbeiterDaten', 'anSchwerbehinderung', 'gueltigBis'),
                      placeholder: 'Ausweis gültig bis',
                      placeholderMode: 'prefix',
                    })}
                  </div>
                `,
              })}
              ${FormGridEntry({
                label: 'Kennzeichnung',
                labelSubtext: 'Aktenzeichen, Ausweisnummer oder Geschäftszeichen',
                render: TextInput({
                  ...props.form.field('mitarbeiterDaten', 'anSchwerbehinderung', 'kennzeichnung'),
                  placeholder: 'Aktenzeichen, Ausweisnummer oder Geschäftszeichen',
                  placeholderMode: 'hide',
                  clearable: true,
                }),
              })}
              ${FormGridEntry({
                label: 'Nachweis Schwerbehinderung',
                render: FileInput({
                  ...props.form.field('mitarbeiterDaten', 'anSchwerbehinderung', 'schwerbehinderungID'),
                  placeholder: 'Kopie Nachweis Schwerbehinderung',
                  placeholderMode: 'prefix',
                }),
              })}
            `
          : nothing
      }
    </div>
  `;
});

export const EmployeeSectionSchwerbehinderungFormValidator = {
  grad: options => {
    if (options.target.istVorhanden) {
      return sgNonOptional()(options);
    }
  },
  dienststelle: options => {
    if (options.target.istVorhanden) {
      return sgNonOptional()(options);
    }
  },
  gueltigVon: options => {
    if (options.target.istVorhanden) {
      return sgNonOptional()(options);
    }
  },
  gueltigBis: options => {
    if (options.target.istVorhanden && options.target.gueltigBis) {
      if (options.target.gueltigBis < options.target.gueltigVon) {
        throw new Error('Das Enddatum muss nach dem Beginndatum liegen.');
      }
    }
  },
  kennzeichnung: options => {
    if (options.target.istVorhanden) {
      return sgNonOptional()(options);
    }
  },
  schwerbehinderungID: options => {
    if (options.target.istVorhanden) {
      return sgNonOptional()(options);
    }
  },
};
